import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { BarChart, PieChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import ReactEchart from '../../../../components/common/ReactEchart';
import { useAppContext } from '../../../../providers/AppProvider';
import StatisticsCardLoading from '../../common/statistic-chart-card/StatisticsChartCardLoading';
import StatisticsChartEmpty from '../../common/StatisticsChartEmpty';
import { STATISTICS_EMPTY_CHART } from '../../common/lib/statisticsEmptyStateCopy';
import StatisticsCount from '../../common/StatisticsCount';
import { applyChartSummaryLegend, enrichMetricLegendWithSummary } from '../shared/lib/chartSummaryLegend';
import StatisticsDonutLabelModeSwitch from '../../common/StatisticsDonutLabelModeSwitch';
import useStatisticsChartTheme from '../../common/hooks/useStatisticsChartTheme';
import StatisticsChartFrame from '../../common/StatisticsChartFrame';
import { buildDashboardPresetPlotLoadingStyle } from '../board/lib/dashboardPresetLayout';
import { ORDER_PRIMARY_PHASE_FLOW_META } from './lib/orderPrimaryPhaseConfig';
import OrderPrimaryPhaseOrderCountLink from './OrderPrimaryPhaseOrderCountLink';
import {
	buildPrimaryPhaseMetricTooltipLine,
	formatPrimaryPhaseMetricAxisValue,
	formatPrimaryPhaseMetricValue,
	formatPipelinePhaseAmount,
	getPrimaryPhaseMetricLabel,
	getPrimaryPhaseOverviewMetricValue,
	getPrimaryPhaseRowMetricValue,
	isPrimaryPhaseCurrencyMetric,
	ORDER_PRIMARY_PHASE_METRICS,
} from './lib/orderPrimaryPhaseMetric';
import {
	DONUT_PIE_LABEL_MODE,
	getPrimaryPhaseChartTheme,
	mapDonutPieDataWithLabel,
	resolveDonutPieRadius,
	resolveDualPhasePieRadii,
} from './lib/orderPrimaryPhaseChartTheme';

echarts.use([TooltipComponent, GridComponent, LegendComponent, BarChart, PieChart, CanvasRenderer]);

const DUAL_PIE_METRICS = [ORDER_PRIMARY_PHASE_METRICS.COUNT, ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE];

const ProcessDistributionCharts = ({
	lensData,
	isLoading,
	selectedPhase,
	onSelectPhase,
	activeMetric = 'count',
	animationKey = 'default',
	barOnly = false,
	pieOnly = false,
	dualPhaseMetric = false,
	barHeight = '14rem',
	pieHeight = '14rem',
	fillSlot = false,
	enableOrderDrill = false,
	onDrillSelect,
	/** 嵌入经营总览旁：不重复圆心总额，默认自选标签 */
	embedded = false,
	centerHint = '流程',
	initialLabelMode,
	/** 固定主阶段扇区顺序；为零的阶段不占扇区，但顺序始终按流程元数据 */
	fixedSegments = false,
}) => {
	const { getThemeColor } = useAppContext();
	const { isLight } = useStatisticsChartTheme();
	const chartTheme = useMemo(() => getPrimaryPhaseChartTheme(isLight), [isLight]);
	const overview = lensData?.overview ?? {};
	const metricLabel = getPrimaryPhaseMetricLabel(activeMetric);
	const currencyMetric = isPrimaryPhaseCurrencyMetric(activeMetric);

	const phaseMap = useMemo(() => Object.fromEntries((lensData?.phases ?? []).map((row) => [row.phase, row])), [lensData?.phases]);

	const phaseRows = useMemo(
		() =>
			ORDER_PRIMARY_PHASE_FLOW_META.map(({ phase, label, colorType }) => {
				const row = phaseMap[phase] ?? {};
				return {
					phase,
					label,
					colorType,
					count: row.count ?? 0,
					totalPrice: row.totalPrice ?? 0,
					receivableTotal: row.receivableTotal ?? 0,
					quasiReceivableTotal: row.quasiReceivableTotal ?? 0,
					pendingPrepaymentTotal: row.pendingPrepaymentTotal ?? 0,
					metricValue: getPrimaryPhaseRowMetricValue(row, activeMetric),
				};
			}),
		[activeMetric, phaseMap],
	);

	const overviewOrderCount = getPrimaryPhaseOverviewMetricValue(overview, ORDER_PRIMARY_PHASE_METRICS.COUNT);
	const overviewContractAmount = getPrimaryPhaseOverviewMetricValue(overview, ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE);
	const overviewMetricValue = getPrimaryPhaseOverviewMetricValue(overview, activeMetric);

	const formatDualPieTooltip = useCallback((params) => {
		const row = params.data?.row;
		const metric = params.data?.metric;
		if (!metric || !row) return params.name ?? '';
		const metricLabelText = getPrimaryPhaseMetricLabel(metric);
		const valueText = formatPrimaryPhaseMetricValue(params.value, metric);
		const suffix = metric === ORDER_PRIMARY_PHASE_METRICS.COUNT ? ' 单' : '';
		return `${params.name}<br/>${metricLabelText} ${valueText}${suffix} (${params.percent}%)`;
	}, []);

	const [labelMode, setLabelMode] = useState(
		() => initialLabelMode ?? (embedded ? DONUT_PIE_LABEL_MODE.PICK : DONUT_PIE_LABEL_MODE.ALL),
	);
	const [pickedLabelKeys, setPickedLabelKeys] = useState(() => new Set());
	const [localSelectedPhase, setLocalSelectedPhase] = useState(null);
	const phaseSelectionControlled = typeof onSelectPhase === 'function';
	const effectiveSelectedPhase = phaseSelectionControlled ? selectedPhase : localSelectedPhase;
	const selectedPhaseLabel = useMemo(
		() => phaseRows.find((row) => row.phase === effectiveSelectedPhase)?.label ?? null,
		[effectiveSelectedPhase, phaseRows],
	);

	useEffect(() => {
		setLocalSelectedPhase(null);
	}, [animationKey]);

	const summarySalesTotal = useMemo(() => {
		if (!effectiveSelectedPhase) return overviewContractAmount;
		return phaseRows.find((row) => row.phase === effectiveSelectedPhase)?.totalPrice ?? 0;
	}, [effectiveSelectedPhase, overviewContractAmount, phaseRows]);

	const summaryOrderCount = useMemo(() => {
		if (!effectiveSelectedPhase) return overviewOrderCount;
		return phaseRows.find((row) => row.phase === effectiveSelectedPhase)?.count ?? 0;
	}, [effectiveSelectedPhase, overviewOrderCount, phaseRows]);

	const summaryMetricValue = useMemo(() => {
		if (!effectiveSelectedPhase) return overviewMetricValue;
		const row = phaseRows.find((item) => item.phase === effectiveSelectedPhase);
		return row ? getPrimaryPhaseRowMetricValue(row, activeMetric) : 0;
	}, [activeMetric, effectiveSelectedPhase, overviewMetricValue, phaseRows]);

	const handlePhaseClick = useCallback(
		(params) => {
			const phase = params?.data?.phase;
			if (!phase) return;
			const row = phaseRows.find((item) => item.phase === phase);
			if (enableOrderDrill && onDrillSelect) {
				onDrillSelect({
					kind: 'phase',
					phase,
					label: row?.label ?? phase,
				});
				return;
			}
			if (phaseSelectionControlled) {
				onSelectPhase(phase);
			} else {
				setLocalSelectedPhase((prev) => (prev === phase ? null : phase));
			}
			if (!embedded && labelMode === DONUT_PIE_LABEL_MODE.PICK) {
				setPickedLabelKeys((prev) => {
					const next = new Set(prev);
					if (next.has(phase)) next.delete(phase);
					else next.add(phase);
					return next;
				});
			}
		},
		[embedded, enableOrderDrill, labelMode, onDrillSelect, onSelectPhase, phaseRows, phaseSelectionControlled],
	);
	const handlePhaseClickRef = useRef(handlePhaseClick);
	handlePhaseClickRef.current = handlePhaseClick;
	const chartEvents = useMemo(
		() => ({
			click: (...args) => handlePhaseClickRef.current?.(...args),
		}),
		[],
	);

	const handleLabelModeChange = useCallback((mode) => {
		setLabelMode(mode);
		if (mode === DONUT_PIE_LABEL_MODE.ALL) setPickedLabelKeys(new Set());
	}, []);
	const barFrameStyle = useMemo(
		() => (fillSlot ? { height: '100%', minHeight: 0 } : { minHeight: barHeight }),
		[barHeight, fillSlot],
	);
	const barChartStyle = useMemo(
		() => (fillSlot ? { width: '100%', height: '100%', minHeight: 0 } : { minHeight: barHeight }),
		[barHeight, fillSlot],
	);
	const pieFrameStyle = useMemo(
		() => (fillSlot ? { height: '100%', minHeight: 0 } : { height: pieHeight, minHeight: pieHeight }),
		[fillSlot, pieHeight],
	);
	const pieChartStyle = useMemo(
		() => (fillSlot ? { width: '100%', height: '100%', minHeight: 0 } : { width: '100%', height: pieHeight, minHeight: pieHeight }),
		[fillSlot, pieHeight],
	);

	const showDualPhaseBar = barOnly && dualPhaseMetric && (activeMetric === ORDER_PRIMARY_PHASE_METRICS.COUNT || activeMetric === ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE);
	const showDualPhasePie = pieOnly && dualPhaseMetric && activeMetric === ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE;

	const dualPhaseBarOption = useMemo(
		() => ({
			backgroundColor: 'transparent',
			legend: enrichMetricLegendWithSummary(
				{
					data: ['单数', '合同金额'],
					top: 0,
					right: 0,
					itemWidth: 10,
					itemHeight: 8,
					textStyle: { color: chartTheme.labelColor, fontSize: 10 },
				},
				chartTheme,
				summarySalesTotal,
				summaryOrderCount,
			),
			grid: { left: 6, right: 28, top: 28, bottom: 8, containLabel: true },
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'axis',
				axisPointer: { type: 'shadow', shadowStyle: { color: chartTheme.axisPointerShadow } },
				formatter: (items) => {
					const item = items?.[0];
					if (!item) return '';
					const row = phaseRows[item.dataIndex];
					return [`${row.label}`, `单数 ${row.count} 单`, `金额 ${formatPipelinePhaseAmount(row.totalPrice)}`].join('<br/>');
				},
			},
			xAxis: {
				type: 'category',
				data: phaseRows.map((r) => r.label),
				...chartTheme.axis,
				axisLabel: {
					...chartTheme.axis.axisLabel,
					fontSize: 10,
					interval: 0,
					rotate: phaseRows.length > 4 ? 24 : 0,
				},
				splitLine: { show: false },
			},
			yAxis: [
				{
					type: 'value',
					minInterval: 1,
					position: 'left',
					...chartTheme.axis,
					axisLabel: { ...chartTheme.axis.axisLabel, fontSize: 9 },
				},
				{
					type: 'value',
					position: 'right',
					...chartTheme.axis,
					axisLabel: {
						...chartTheme.axis.axisLabel,
						fontSize: 9,
						formatter: formatPrimaryPhaseMetricAxisValue,
					},
				},
			],
			series: [
				{
					name: '单数',
					type: 'bar',
					yAxisIndex: 0,
					data: phaseRows.map((row) => ({
						value: row.count,
						phase: row.phase,
						itemStyle: chartTheme.createBarStyle(getThemeColor(row.colorType), effectiveSelectedPhase, row.phase),
					})),
					barMaxWidth: 9,
					emphasis: chartTheme.barEmphasis,
				},
				{
					name: '合同金额',
					type: 'bar',
					yAxisIndex: 1,
					data: phaseRows.map((row) => ({
						value: row.totalPrice,
						phase: row.phase,
						itemStyle: {
							...chartTheme.createBarStyle(getThemeColor(row.colorType), effectiveSelectedPhase, row.phase),
							opacity: 0.72,
						},
					})),
					barMaxWidth: 9,
					emphasis: chartTheme.barEmphasis,
				},
			],
		}),
		[chartTheme, getThemeColor, phaseRows, effectiveSelectedPhase, summaryOrderCount, summarySalesTotal],
	);

	const barOption = useMemo(
		() => ({
			backgroundColor: 'transparent',
			grid: {
				left: 4,
				right: embedded ? 8 : 12,
				top: embedded ? 18 : 24,
				bottom: embedded ? 4 : 6,
				containLabel: true,
			},
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'axis',
				axisPointer: { type: 'shadow', shadowStyle: { color: chartTheme.axisPointerShadow } },
				formatter: (items) => {
					const item = items?.[0];
					if (!item) return '';
					const row = phaseRows[item.dataIndex];
					return [row.label, formatPrimaryPhaseMetricValue(row.metricValue, activeMetric)].join('<br/>');
				},
			},
			xAxis: {
				type: 'category',
				data: phaseRows.map((r) => r.label),
				...chartTheme.axis,
				axisLabel: {
					...chartTheme.axis.axisLabel,
					fontSize: embedded ? 11 : 10,
					interval: 0,
					rotate: phaseRows.length > 4 ? 24 : 0,
				},
				splitLine: { show: false },
			},
			yAxis: {
				type: 'value',
				...(currencyMetric ? {} : { minInterval: 1 }),
				...chartTheme.axis,
				axisLabel: {
					...chartTheme.axis.axisLabel,
					formatter: currencyMetric ? formatPrimaryPhaseMetricAxisValue : undefined,
				},
			},
			series: [
				{
					name: '本期',
					type: 'bar',
					data: phaseRows.map((row) => ({
						value: row.metricValue,
						phase: row.phase,
						itemStyle: chartTheme.createBarStyle(getThemeColor(row.colorType), effectiveSelectedPhase, row.phase),
					})),
					barMaxWidth: embedded ? 28 : 24,
					barCategoryGap: embedded ? '30%' : undefined,
					emphasis: chartTheme.barEmphasis,
					label: {
						show: true,
						position: 'top',
						fontSize: embedded ? 11 : 10,
						color: chartTheme.labelColor,
						formatter: (params) => formatPrimaryPhaseMetricValue(params.value, activeMetric),
					},
					animationDuration: 500,
					animationEasing: 'cubicOut',
				},
			],
		}),
		[activeMetric, chartTheme, currencyMetric, embedded, getThemeColor, phaseRows, effectiveSelectedPhase],
	);

	const chartSummary = useMemo(
		() => ({ salesTotal: summarySalesTotal, orderCount: summaryOrderCount }),
		[summaryOrderCount, summarySalesTotal],
	);
	const barDisplayOption = useMemo(() => {
		if (embedded) return barOption;
		return applyChartSummaryLegend(barOption, chartSummary, chartTheme);
	}, [barOption, chartSummary, chartTheme, embedded]);

	const pieData = useMemo(() => {
		if (!fixedSegments) {
			return phaseRows
				.filter((row) => Number(row.metricValue) > 0)
				.map((row) => ({
					phase: row.phase,
					name: row.label,
					value: Number(row.metricValue) || 0,
					displayValue: Number(row.metricValue) || 0,
					row,
					itemStyle: { color: getThemeColor(row.colorType) },
				}));
		}

		// 固定 6 段等分扇区：角度不跟指标走，指标只用于着色深浅与 tooltip
		const displayTotal = phaseRows.reduce((sum, row) => sum + (Number(row.metricValue) || 0), 0);
		return phaseRows.map((row) => {
			const displayValue = Number(row.metricValue) || 0;
			const isEmpty = !(displayValue > 0);
			const share = displayTotal > 0 ? displayValue / displayTotal : 0;
			return {
				phase: row.phase,
				name: row.label,
				value: 1,
				displayValue,
				isEmpty,
				row,
				itemStyle: {
					color: getThemeColor(row.colorType),
					// 无值更淡；有值按占比略提不透明度，但仍保持段可见
					opacity: isEmpty ? 0.2 : Math.max(0.55, Math.min(1, 0.55 + share * 0.45)),
				},
			};
		});
	}, [fixedSegments, getThemeColor, phaseRows]);

	const pieDisplayTotal = useMemo(
		() => pieData.reduce((sum, item) => sum + (Number(item.displayValue) || 0), 0),
		[pieData],
	);

	const buildPieDataForMetric = useCallback(
		(metric) =>
			phaseRows
				.filter((row) => getPrimaryPhaseRowMetricValue(row, metric) > 0)
				.map((row) => ({
					phase: row.phase,
					name: row.label,
					value: getPrimaryPhaseRowMetricValue(row, metric),
					row,
					metric,
					itemStyle: { color: getThemeColor(row.colorType) },
				})),
		[getThemeColor, phaseRows],
	);

	const pieDataByMetric = useMemo(() => (showDualPhasePie ? DUAL_PIE_METRICS.map((metric) => buildPieDataForMetric(metric)) : []), [buildPieDataForMetric, showDualPhasePie]);

	const pieHasData = showDualPhasePie
		? pieDataByMetric.some((items) => items.length > 0)
		: fixedSegments
			? phaseRows.length > 0
			: pieData.length > 0;

	const pieHasLabelRoom = labelMode === DONUT_PIE_LABEL_MODE.ALL || pickedLabelKeys.size > 0;
	const pieCompact = parseFloat(pieHeight) <= 7;

	useEffect(() => {
		setPickedLabelKeys((prev) => {
			const valid = new Set(showDualPhasePie ? pieDataByMetric.flatMap((items) => items.map((item) => item.phase)) : pieData.map((item) => item.phase));
			const next = new Set([...prev].filter((key) => valid.has(key)));
			return next.size === prev.size ? prev : next;
		});
	}, [pieData, pieDataByMetric, showDualPhasePie]);

	const pieRadius = useMemo(() => {
		if (showDualPhasePie) {
			return resolveDualPhasePieRadii({
				needsLabelRoom: pieHasLabelRoom,
				compact: pieCompact,
			});
		}
		if (embedded) {
			// 克制大气：环略厚、中心留白充足
			if (pieHasLabelRoom) return [['42%', '66%']];
			return [['50%', '74%']];
		}
		return [resolveDonutPieRadius(pieHasLabelRoom, pieCompact, { largeCenter: currencyMetric })];
	}, [currencyMetric, embedded, pieCompact, pieHasLabelRoom, showDualPhasePie]);

	const isPieSliceSelected = useCallback(
		(item) => {
			if (effectiveSelectedPhase != null) return item.phase === effectiveSelectedPhase;
			return labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.has(item.phase);
		},
		[effectiveSelectedPhase, labelMode, pickedLabelKeys],
	);

	const pieOption = useMemo(() => {
		if (showDualPhasePie) {
			const hasMetricLegend = DUAL_PIE_METRICS.length > 1;
			return {
				backgroundColor: 'transparent',
				tooltip: {
					...chartTheme.tooltip,
					trigger: 'item',
					formatter: formatDualPieTooltip,
				},
				legend: hasMetricLegend
					? enrichMetricLegendWithSummary(
							{
								show: true,
								top: 0,
								left: 'center',
								data: DUAL_PIE_METRICS.map((metric) => getPrimaryPhaseMetricLabel(metric)),
								itemWidth: 8,
								itemHeight: 8,
								textStyle: { color: chartTheme.labelColor, fontSize: 10 },
							},
							chartTheme,
							summarySalesTotal,
							summaryOrderCount,
						)
					: { show: false },
				series: DUAL_PIE_METRICS.map((metric, ringIndex) => {
					const ringData = pieDataByMetric[ringIndex] ?? [];
					const isLabeledRing = metric === ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE;
					const hasSliceSelection = effectiveSelectedPhase != null;
					const hasPicked = labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.size > 0;
					const mappedData = isLabeledRing
						? mapDonutPieDataWithLabel(ringData, {
								chartTheme,
								getKey: (item) => item.phase,
								labelMode,
								pickedKeys: pickedLabelKeys,
								selectedKey: effectiveSelectedPhase,
								labelFormatter: (params) => `${params.name}\n${formatPrimaryPhaseMetricValue(params.value, metric)} · ${params.percent}%`,
							})
						: ringData.map((item) => {
								const labelEmphasized = labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.has(item.phase);
								const sliceSelected = isPieSliceSelected(item);
								const emphasized = hasSliceSelection ? sliceSelected : labelEmphasized;
								return {
									...item,
									itemStyle: {
										...item.itemStyle,
										opacity: (hasSliceSelection || hasPicked) && !emphasized ? 0.38 : 1,
										shadowBlur: hasSliceSelection && sliceSelected ? 10 : 0,
										shadowColor: hasSliceSelection && sliceSelected ? chartTheme.pieItemShadow : 'transparent',
									},
								};
							});
					return {
						name: getPrimaryPhaseMetricLabel(metric),
						type: 'pie',
						radius: pieRadius[ringIndex],
						center: ['50%', hasMetricLegend ? '52%' : '50%'],
						padAngle: 2,
						sort: 'none',
						color: ringData.map((item) => item.itemStyle.color),
						selectedMode: false,
						avoidLabelOverlap: true,
						label: { show: false },
						labelLine: { show: false },
						cursor: 'pointer',
						z: ringIndex + 1,
						data: mappedData,
						animationDuration: 700,
					};
				}),
			};
		}

		const mappedPieData = mapDonutPieDataWithLabel(pieData, {
			chartTheme,
			getKey: (item) => item.phase,
			labelMode,
			pickedKeys: pickedLabelKeys,
			selectedKey: effectiveSelectedPhase,
			labelFormatter: embedded
				? (params) => {
						const raw = params.data?.displayValue ?? params.value;
						const truePercent = pieDisplayTotal > 0 ? Math.round((Number(raw) / pieDisplayTotal) * 1000) / 10 : 0;
						return `${params.name}\n${truePercent}%`;
					}
				: (params) => `${params.name}\n${formatPrimaryPhaseMetricValue(params.value, activeMetric)} · ${params.percent}%`,
		}).map((item) => {
			if (!embedded) return item;
			const emptySlice = Boolean(item.isEmpty);
			return {
				...item,
				itemStyle: {
					...item.itemStyle,
					borderRadius: 10,
					borderColor: chartTheme.pieBorderColor,
					borderWidth: 2,
					opacity: emptySlice
						? 0.22
						: item.itemStyle?.opacity === 0.38
							? 0.28
							: item.itemStyle?.opacity ?? 1,
					shadowBlur: !emptySlice && item.itemStyle?.shadowBlur ? 14 : 0,
				},
				label: item.label
					? {
							...item.label,
							fontSize: 11,
							fontWeight: 600,
							lineHeight: 15,
							// 空阶段不挂标签，避免噪音
							show: emptySlice ? false : item.label.show,
						}
					: item.label,
				labelLine: item.labelLine
					? {
							...item.labelLine,
							show: emptySlice ? false : item.labelLine.show,
							length: 12,
							length2: 10,
							lineStyle: { ...(item.labelLine.lineStyle ?? {}), width: 1.25, opacity: 0.75 },
						}
					: item.labelLine,
			};
		});

		return {
			backgroundColor: 'transparent',
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'item',
				// 挂到 body 避免被图表 overflow 裁切；再用 position 限制在视口内
				...(embedded
					? {
							appendToBody: true,
							confine: false,
							extraCssText: `${chartTheme.tooltip?.extraCssText ?? ''} z-index: 10050; max-width: min(20rem, calc(100vw - 1rem));`.trim(),
							position: (pos, _params, _dom, _rect, size) => {
								const gap = 12;
								const pad = 8;
								const [mx, my] = pos;
								const cw = size?.contentSize?.[0] ?? 0;
								const ch = size?.contentSize?.[1] ?? 0;
								const vw = window.innerWidth;
								const vh = window.innerHeight;
								let x = mx + gap;
								let y = my + gap;
								if (x + cw > vw - pad) x = mx - cw - gap;
								if (x < pad) x = pad;
								if (x + cw > vw - pad) x = Math.max(pad, vw - cw - pad);
								if (y + ch > vh - pad) y = my - ch - gap;
								if (y < pad) y = pad;
								if (y + ch > vh - pad) y = Math.max(pad, vh - ch - pad);
								return [x, y];
							},
						}
					: { confine: true }),
				formatter: (p) => {
					const row = p.data?.row;
					const rawValue = p.data?.displayValue ?? p.value;
					const valueText = formatPrimaryPhaseMetricValue(rawValue, activeMetric);
					const truePercent =
						pieDisplayTotal > 0 ? Math.round((Number(rawValue) / pieDisplayTotal) * 1000) / 10 : 0;
					return `${p.name}<br/>${metricLabel} ${valueText} (${truePercent}%)<br/>${buildPrimaryPhaseMetricTooltipLine(row, activeMetric).split('<br/>').slice(1).join('<br/>')}`;
				},
			},
			series: [
				{
					type: 'pie',
					radius: pieRadius[0],
					center: ['50%', '50%'],
					padAngle: embedded || fixedSegments ? 3.5 : 2,
					minAngle: 0,
					sort: 'none',
					color: pieData.map((item) => item.itemStyle.color),
					selectedMode: false,
					avoidLabelOverlap: true,
					label: { show: false },
					labelLine: { show: false },
					cursor: 'pointer',
					data: mappedPieData,
					animationDuration: embedded ? 850 : 700,
					animationEasing: 'cubicOut',
				},
			],
		};
	}, [
		activeMetric,
		chartTheme,
		effectiveSelectedPhase,
		embedded,
		fixedSegments,
		formatDualPieTooltip,
		isPieSliceSelected,
		labelMode,
		metricLabel,
		pickedLabelKeys,
		pieData,
		pieDataByMetric,
		pieDisplayTotal,
		pieRadius,
		showDualPhasePie,
		summaryOrderCount,
		summarySalesTotal,
	]);

	if (isLoading) {
		const loadingHeight = barOnly ? barHeight : pieOnly ? pieHeight : barHeight;
		return (
			<StatisticsCardLoading
				className={['statistics-lite-chart__loading', fillSlot ? 'statistics-lite-chart__loading--fill' : null].filter(Boolean).join(' ')}
				style={buildDashboardPresetPlotLoadingStyle(loadingHeight, { fillSlot })}
				fill={fillSlot}
			/>
		);
	}

	if (barOnly) {
		const option = showDualPhaseBar ? dualPhaseBarOption : barDisplayOption;
		return (
			<div
				className={['statistics-pipeline-preset-chart', fillSlot ? 'statistics-pipeline-preset-chart--fill' : null].filter(Boolean).join(' ')}
			>
				<StatisticsChartFrame fillSlot={fillSlot} style={barFrameStyle}>
					<ReactEchart
						key={`bar-${showDualPhaseBar ? 'dual' : 'single'}-${animationKey}`}
						echarts={echarts}
						option={option}
						onEvents={chartEvents}
						style={barChartStyle}
					/>
				</StatisticsChartFrame>
			</div>
		);
	}

	if (pieOnly) {
		if (!pieHasData) return <StatisticsChartEmpty>{STATISTICS_EMPTY_CHART}</StatisticsChartEmpty>;
		const pieWrapClass = [
			'statistics-lite-chart__pie-wrap',
			embedded ? 'statistics-lite-chart__pie-wrap--embedded' : null,
			embedded ? 'statistics-lite-chart__pie-wrap--premium' : null,
			pieHasLabelRoom ? 'statistics-lite-chart__pie-wrap--selected' : null,
			showDualPhasePie ? 'statistics-lite-chart__pie-wrap--dual-metric' : null,
			fillSlot ? 'statistics-lite-chart__pie-wrap--fill' : null,
		]
			.filter(Boolean)
			.join(' ');
		return (
			<div className={pieWrapClass}>
				{embedded ? null : <StatisticsDonutLabelModeSwitch mode={labelMode} onChange={handleLabelModeChange} />}
				<StatisticsChartFrame
					fillSlot={fillSlot}
					className={pieHasLabelRoom ? 'statistics-chart-frame--donut-selected' : ''}
					style={pieFrameStyle}
				>
					<ReactEchart
						key={`pie-${showDualPhasePie ? 'dual' : 'single'}-${animationKey}`}
						echarts={echarts}
						option={pieOption}
						onEvents={chartEvents}
						style={pieChartStyle}
					/>
				</StatisticsChartFrame>
				<div
					className={[
						'statistics-lite-chart__pie-center',
						embedded ? 'statistics-lite-chart__pie-center--hint' : null,
						!embedded && showDualPhasePie ? 'statistics-lite-chart__pie-center--dual-metric' : null,
					]
						.filter(Boolean)
						.join(' ')}
				>
					{embedded ? (
						<strong className='statistics-lite-chart__pie-center-link statistics-lite-chart__pie-center-anchor'>
							<span className='statistics-lite-chart__pie-center-anchor-label'>{selectedPhaseLabel || centerHint}</span>
							{selectedPhaseLabel ? <span className='statistics-lite-chart__pie-center-anchor-hint'>已选</span> : null}
						</strong>
					) : showDualPhasePie ? (
						<strong className='statistics-lite-chart__pie-center-link statistics-lite-chart__pie-center-dual'>
							<span className='statistics-lite-chart__pie-center-metric-row'>
								<span className='statistics-lite-chart__pie-center-metric-hint'>合同金额</span>
								<StatisticsCount value={summarySalesTotal} loading={isLoading} mode='currency' duration={0.45} />
							</span>
							<span className='statistics-lite-chart__pie-center-metric-row'>
								<span className='statistics-lite-chart__pie-center-metric-hint'>单数</span>
								<span className='statistics-lite-chart__pie-center-count-row'>
									<StatisticsCount value={summaryOrderCount} loading={isLoading} duration={0.45} />
									<span className='statistics-lite-chart__pie-center-count-suffix'>单</span>
								</span>
							</span>
						</strong>
					) : currencyMetric ? (
						<strong className='statistics-lite-chart__pie-center-link statistics-lite-chart__pie-center-currency'>
							<StatisticsCount value={summaryMetricValue} loading={isLoading} mode='currency' duration={0.45} />
						</strong>
					) : (
						<OrderPrimaryPhaseOrderCountLink
							count={summaryMetricValue}
							phase={null}
							selectedPhase={effectiveSelectedPhase}
							onSelectPhase={onSelectPhase}
							className='statistics-lite-chart__pie-center-link'
						/>
					)}
				</div>
			</div>
		);
	}

	return null;
};

ProcessDistributionCharts.propTypes = {
	lensData: PropTypes.object,
	isLoading: PropTypes.bool,
	selectedPhase: PropTypes.string,
	onSelectPhase: PropTypes.func,
	activeMetric: PropTypes.string,
	animationKey: PropTypes.string,
	barOnly: PropTypes.bool,
	pieOnly: PropTypes.bool,
	dualPhaseMetric: PropTypes.bool,
	barHeight: PropTypes.string,
	pieHeight: PropTypes.string,
	fillSlot: PropTypes.bool,
	enableOrderDrill: PropTypes.bool,
	onDrillSelect: PropTypes.func,
	embedded: PropTypes.bool,
	centerHint: PropTypes.string,
	initialLabelMode: PropTypes.oneOf(['all', 'pick']),
	fixedSegments: PropTypes.bool,
};

export default memo(ProcessDistributionCharts);
